import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { SivaBudgetDetailsDto } from 'src/api/v2';
import ContentPage from 'src/components/ContentPage/ContentPage';
import DataTableV3, { DataTableV3Actions } from 'src/components/DataTableV3';
import { LoadingPage } from 'src/components/LoadingPage/LoadingPage';
import { useSivaBudgetDetailsList } from '../../hooks/use-siva-budget-details-list';
import '../../scss/SivaAdminBudget.scss';
import { budgetYearColumns } from '../constants/budget-year-columns';
import { DataTableV3Ids } from 'src/constants/dataTableIds';

type PageParams = {
  year: string;
};

const SivaAdminBudgetYearPage: React.FC = () => {
  const { year } = useParams<PageParams>();

  const { t: tCommon } = useTranslation();
  const { t: tPageTitles } = useTranslation('pageTitles');

  const documentTitle = `${tCommon('budget')} - ${tPageTitles('sivaReporting')}`;

  const { sivaBudgets, isLoading, mutations } = useSivaBudgetDetailsList(year ? parseInt(year) : undefined);

  const actions = useMemo(() => {
    const actions: DataTableV3Actions<SivaBudgetDetailsDto> = {
      secondary: [
        {
          label: 'Godkjenn',
          icon: { prefix: 'fasr', iconName: 'check-circle' },
          isLoading: mutations.approve.isLoading,
          disabled: mutations.approve.isLoading || mutations.reject.isLoading,
          onClick: async (data) => {
            const check = data.some((item) => item.sivaBudgetAttachments.length === 0);
            if (check) {
              toast.error('Budsjettene må være levert før de kan godkjennes');
              return false;
            }

            const promises = data.map((item) => mutations.approve.mutateAsync(item.id));
            const promise = Promise.all(promises);

            await toast.promise(promise, {
              pending: 'Godkjenner budsjettene...',
              success: 'Budsjettene ble godkjent',
              error: 'Kunne ikke godkjenne budsjettene',
            });

            return true;
          },
        },
        {
          label: 'Send tilbake',
          icon: { prefix: 'fasr', iconName: 'arrow-left' },
          isLoading: mutations.reject.isLoading,
          disabled: mutations.approve.isLoading || mutations.reject.isLoading,
          onClick: async (data) => {
            const check = data.some((item) => item.sivaBudgetAttachments.length === 0);
            if (check) {
              toast.error('Budsjettene må være levert før de kan sendes tilbake');
              return false;
            }

            const promises = data.map((item) => mutations.reject.mutateAsync(item.id));
            const promise = Promise.all(promises);

            await toast.promise(promise, {
              pending: 'Sender tilbake budsjettene...',
              success: 'Budsjettene ble sendt tilbake',
              error: 'Kunne ikke sende budsjettene tilbake',
            });

            return true;
          },
        },
      ],
    };

    return actions;
  }, [mutations.approve, mutations.reject]);

  if (!year || !year.match(/[2][0-9]{3}/)) {
    return <ContentPage title="Ugyldig år" />;
  }

  if (isLoading) {
    // eslint-disable-next-line react/jsx-no-undef
    return <LoadingPage aria-label="Laster data..." />;
  }

  return (
    <ContentPage title={`${tCommon('budget')} ${year}`} documentTitle={documentTitle}>
      <div className="siva-admin-budget__table">
        <DataTableV3
          id={DataTableV3Ids.SivaAdmin.BudgetPerYear}
          data={sivaBudgets}
          columns={budgetYearColumns}
          actions={actions}
          pageSizeOptions={[10, 25, 50, 100]}
          disableColumnEditor
          disableExport
        />
      </div>
    </ContentPage>
  );
};

export default SivaAdminBudgetYearPage;
